import React, { useEffect } from "react";
import { Modal, Form, Input, Button, Select, Checkbox, Row, Col, message, Collapse } from "antd";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../redux/createAiCredential/action";
import userActions from "../../../redux/user/actions";

const boolOptions = [
    { label: "True", value: true },
    { label: "False", value: false },
];

const { Panel } = Collapse;

const UpdateAiDetailsForm = ({ visible, onCancel, editingRecord }) => {
    const [form] = Form.useForm();
    const [messageApi, contextHolder] = message.useMessage();
    const dispatch = useDispatch();

    const { buttonLoading, messages, error } = useSelector(
        (state) => state?.createAiCredentialReducer || {}
    );
    const modulesData = useSelector((state) => state?.userReducer?.modulesData || []);

    // Fetch modules on mount
    useEffect(() => {
        if (visible) {
            dispatch(userActions.getModules());
        }
    }, [visible, dispatch]);

    // Populate form when editingRecord and modulesData are loaded
    useEffect(() => {
        if (visible && editingRecord && modulesData && modulesData.length > 0) {
            const recordPermissions = editingRecord.permissions || [];
            const formPermissions = modulesData.map((mod) => {
                const existing = recordPermissions.find((p) => {
                    const id = typeof p.moduleId === "object" && p.moduleId !== null ? p.moduleId._id : p.moduleId;
                    return id === mod._id;
                });

                const read = existing ? !!existing.read : false;
                const create = existing ? !!existing.create : false;
                const update = existing ? !!existing.update : false;
                const deleteVal = existing ? !!existing.delete : false;
                const access = read && create && update && deleteVal;

                return {
                    moduleId: mod._id,
                    module: mod.moduleName,
                    access,
                    read,
                    create,
                    update,
                    delete: deleteVal,
                };
            });

            form.setFieldsValue({
                ...editingRecord,
                permissions: formPermissions,
            });
        }
    }, [visible, editingRecord, modulesData, form]);

    // Success handler
    useEffect(() => {
        if (messages) {
            messageApi.success(messages);
            dispatch(actions.clearUserLoginMessages()); // reset messages in store
            form.resetFields();
            onCancel();
        }
    }, [messages, messageApi, dispatch, form, onCancel]);

    // Error handler
    useEffect(() => {
        if (error) {
            messageApi.error(error);
            dispatch(actions.clearUserLoginMessages()); // reset error in store
        }
    }, [error, messageApi, dispatch]);

    const handleAccessChange = (fieldName, checked) => {
        const permissions = form.getFieldValue("permissions") || [];
        if (permissions[fieldName]) {
            permissions[fieldName].read = checked;
            permissions[fieldName].create = checked;
            permissions[fieldName].update = checked;
            permissions[fieldName].delete = checked;
            permissions[fieldName].access = checked;
            form.setFieldsValue({ permissions });
        }
    };

    const handlePermissionCheckboxChange = (fieldName, key, checked) => {
        const permissions = form.getFieldValue("permissions") || [];
        const permRow = permissions[fieldName];
        if (permRow) {
            permRow[key] = checked;
            if (permRow.create || permRow.update || permRow.delete) {
                permRow.read = true;
            } else {
                permRow.read = false;
            }
            permRow.access = permRow.read && permRow.create && permRow.update && permRow.delete;
            form.setFieldsValue({ permissions });
        }
    };

    const handleSubmit = async () => {
        const values = await form.validateFields();
        const formattedPermissions = (values.permissions || []).map((perm) => ({
            moduleId: perm.moduleId,
            create: !!perm.create,
            update: !!perm.update,
            read: !!perm.read,
            delete: !!perm.delete,
        }));

        const hasAtLeastOnePermission = formattedPermissions.some(
            (perm) => perm.read || perm.create || perm.update || perm.delete
        );

        if (!hasAtLeastOnePermission) {
            messageApi.error("Please select at least one permission module.");
            return;
        }

        const payload = {
            email: values.email,
            name: values.name,
            internal: values.internal,
            isTwoFactorAuthenticationEnabled: values.isTwoFactorAuthenticationEnabled,
            permissions: formattedPermissions,
        };
        dispatch(actions.updateAiDetails(editingRecord._id, payload));
    };

    return (
        <>
            {contextHolder}
            <Modal
                title="Update AI Details"
                open={visible}
                onCancel={onCancel}
                maskClosable={false}
                width={700}
                footer={[
                    <Button key="cancel" onClick={onCancel}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={buttonLoading}
                        onClick={handleSubmit}
                    >
                        Update
                    </Button>,
                ]}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="Name"
                                rules={[{ required: true, message: "Please enter name" }]}
                            >
                                <Input disabled />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label="Email"
                                rules={[
                                    { required: true, message: "Please enter email" },
                                    { type: "email", message: "Invalid email" },
                                ]}
                            >
                                <Input disabled />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="internal"
                                label="Internal"
                                rules={[{ required: true }]}
                            >
                                <Select options={boolOptions} />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                noStyle
                                dependencies={["isTwoFactorAuthenticationEnabled"]}
                            >
                                {({ getFieldValue }) => {
                                    const is2FaEnabled = getFieldValue("isTwoFactorAuthenticationEnabled");
                                    return (
                                        <Form.Item
                                            name="isTwoFactorAuthenticationEnabled"
                                            label="Two Factor Auth Enabled"
                                            rules={[{ required: true }]}
                                        >
                                            <Select
                                                options={boolOptions}
                                                disabled={is2FaEnabled === false}
                                            />
                                        </Form.Item>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                    </Row>

                    <div style={{ marginTop: 24 }}>
                        <hr />
                        <h4 style={{ marginBottom: 16 }}>Permissions</h4>
                        <div style={{ maxHeight: "350px", overflowY: "auto", paddingRight: "8px" }}>
                            <Form.List name="permissions">
                                {(fields) => (
                                    <Collapse>
                                        {fields.map((field) => (
                                            <Panel
                                                key={field.key}
                                                header={
                                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingRight: "16px" }}>
                                                        <strong>
                                                            {form.getFieldValue([
                                                                "permissions",
                                                                field.name,
                                                                "module",
                                                            ])}
                                                        </strong>
                                                        <div onClick={(e) => e.stopPropagation()}>
                                                            <Form.Item
                                                                name={[field.name, "access"]}
                                                                valuePropName="checked"
                                                                style={{ marginBottom: 0 }}
                                                            >
                                                                <Checkbox
                                                                    onChange={(e) => handleAccessChange(field.name, e.target.checked)}
                                                                >
                                                                    Access
                                                                </Checkbox>
                                                            </Form.Item>
                                                        </div>
                                                    </div>
                                                }
                                            >
                                                <Row gutter={16}>
                                                    {["read", "create", "update", "delete"].map((perm) => (
                                                        <Col key={perm} span={6}>
                                                            <Form.Item
                                                                name={[field.name, perm]}
                                                                valuePropName="checked"
                                                                style={{ marginBottom: 0 }}
                                                            >
                                                                <Checkbox
                                                                    disabled={perm === "read"}
                                                                    onChange={(e) => {
                                                                        if (perm !== "read") {
                                                                            handlePermissionCheckboxChange(field.name, perm, e.target.checked);
                                                                        }
                                                                    }}
                                                                >
                                                                    {perm.charAt(0).toUpperCase() + perm.slice(1)}
                                                                </Checkbox>
                                                            </Form.Item>
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </Panel>
                                        ))}
                                    </Collapse>
                                )}
                            </Form.List>
                        </div>
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default UpdateAiDetailsForm;
